import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { SF_UI } from './ui/tokens';

type ButtonProps = {
  children: ReactNode;
  variant?: 'primary' | 'secondary' | 'outline' | 'text';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  to?: string;
  href?: string;
  target?: string;
  rel?: string;
  as?: 'button' | 'a';
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
};

const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  className = '',
  to,
  href,
  target,
  rel,
  as,
  onClick,
  type = 'button',
  disabled = false,
}: ButtonProps) => {
  const baseClasses =
    `${SF_UI.systemId} sf-cta font-semibold rounded-xl transition-all duration-200 inline-flex items-center justify-center`;

  const variantClasses = {
    primary:
      'bg-primary hover:bg-primary-600 text-white shadow-sm hover:shadow-md shadow-primary/20',
    secondary:
      'bg-secondary hover:bg-secondary-600 text-white shadow-sm hover:shadow-md shadow-secondary/20',
    outline:
      'border border-primary/30 text-primary bg-white hover:bg-primary-50 hover:border-primary/50',
    text: 'text-primary hover:bg-primary-50',
  };

  const sizeClasses = {
    sm: 'text-sm px-3.5 py-1.5',
    md: 'px-5 py-2.5 text-sm',
    lg: 'text-base px-6 py-3.5',
  };

  const disabledClasses = disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer';

  const buttonClasses = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${disabledClasses} ${className}`;

  if (to) {
    return (
      <Link to={to} className={buttonClasses}>
        {children}
      </Link>
    );
  }

  if (as === 'a' || href) {
    return (
      <a href={href || '#'} target={target} rel={rel} className={buttonClasses} onClick={onClick}>
        {children}
      </a>
    );
  }

  return (
    <button type={type} className={buttonClasses} onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );
};

export default Button;
